/**
 * Stateful Macro Coordinator Stores
 *
 * Reactive store carrying the current host-side macro tree walk position so
 * UI / debugging surfaces can subscribe without poking at the coordinator's
 * private fields.
 *
 * Mirrors the pattern used by `apps/host/host-frontend/src/coordinators/game-launch/stores.ts`
 * — coordinator owns the StateStore, exposes a typed view through its public
 * `state` getter, and updates are made through coordinator-internal helpers.
 */

import type { MacroTrigger } from '@playcastdotio/Messaging';
import { makeStateStore } from '@playcastdotio/StateStore';

/**
 * Public, read-only snapshot of the coordinator's walk state.
 *
 * - `active`: true while a walk is in flight (request sent, response not yet
 *   resolved into a terminal outcome). Reset to `false` on any terminal
 *   outcome (`isLeaf` complete, exhausted retries, fallback to legacy, etc.).
 * - `trigger`: which campaign-launch trigger this walk is driving. v1 only
 *   supports `'launch'`; null when no walk is in flight.
 * - `currentNodeId`: node the host believes it just visited, echoed in the
 *   next outbound MACRO_WALK_REQUEST. `null` for the initial request.
 * - `retryCount`: number of `no_match` failures observed so far for the
 *   current campaign attempt. Persists across `stopGame`/restart cycles
 *   driven by the walk so the host self-aborts after the configured budget.
 * - `launchInstanceId`: host-owned id for the current walk attempt. The
 *   coordinator generates a fresh id on every `gameLaunched` and uses it to
 *   drop stale responses (see acceptance criteria).
 * - `waitNodeId` / `waitDeadlineMs`: host-owned Wait polling state. The
 *   deadline is initialized once per Wait node and cleared on every terminal
 *   path or when a child match advances the walk.
 */
export type StatefulMacroWalkState = {
  active: boolean;
  trigger: MacroTrigger | null;
  currentNodeId: string | null;
  retryCount: number;
  launchInstanceId: string | null;
  waitNodeId: string | null;
  waitDeadlineMs: number | null;
};

export const createStatefulMacroWalkStore = () =>
  makeStateStore<StatefulMacroWalkState>({
    active: false,
    trigger: null,
    currentNodeId: null,
    retryCount: 0,
    launchInstanceId: null,
    waitNodeId: null,
    waitDeadlineMs: null,
  });

export type StatefulMacroWalkStore = ReturnType<typeof createStatefulMacroWalkStore>;
